// Session-cookie API client. SPEC section 2 keeps auth in an HttpOnly Django
// session, so there is no token to store and nothing to read from localStorage —
// the browser sends the cookie and we echo the CSRF token back on writes.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly fields: Record<string, string[]> = {},
  ) {
    super(detail)
  }
}

/** Exported because sign-out is a form POST rather than a fetch.
 *
 *  `mozilla_django_oidc.OIDCLogoutView` refuses GET unless
 *  `ALLOW_LOGOUT_GET_METHOD` is on, and leaving it off is the right call: a
 *  logout reachable by GET can be fired by any `<img>` on any page. So the
 *  button posts, and a post needs this token in a hidden field. */
export function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : ''
}

/** Anything that looks like it came from a stack rather than from a person.
 *
 * A 500 should say "something went wrong", not name a module and a line. The
 * server is not supposed to send one of these, and if it ever does — DEBUG left
 * on, a middleware serialising an exception — the screen must not be the thing
 * that publishes it.
 */
const INTERNAL_SHAPE =
  /Traceback|File "|\.py", line \d+|\bat [A-Za-z$_][\w$.]*\s*\(|\b(?:django|psycopg|rest_framework|celery|redis|sqlalchemy)\.[\w.]+/

/** The most useful thing the server said, or a safe generic.
 *
 * Order matters and is the whole point:
 *
 * 1. `detail` — DRF's own word for "here is what went wrong", and what the
 *    permission and throttle layers send.
 * 2. `reason` is appended when present: the change API sends `{detail, reason}`
 *    so an agent is told *which* guard refused, not merely that one did.
 * 3. Otherwise the **first field error**. A 400 from a serializer is
 *    `{"email": ["…already exists."]}` with no `detail` at all, and falling
 *    straight back to `statusText` turned every such refusal into the word
 *    "Bad Request" — the server explaining itself precisely and the screen
 *    discarding it.
 * 4. Otherwise the status text.
 */
export function messageFrom(
  body: unknown,
  status: number,
  statusText: string,
): { detail: string; fields: Record<string, string[]> } {
  const fallback = statusText || `Request failed (${status})`
  if (typeof body !== 'object' || body === null) {
    return { detail: fallback, fields: {} }
  }

  const record = body as Record<string, unknown>
  const fields = Object.fromEntries(
    Object.entries(record)
      .filter(([key]) => key !== 'detail' && key !== 'reason')
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.map(String) : [String(value)],
      ]),
  )

  const stated = [record.detail, record.reason]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ')

  let detail = stated
  if (!detail) {
    const first = Object.values(fields).find((messages) => messages.length > 0)
    if (first) detail = first[0]
  }
  if (!detail) detail = fallback

  // Never let an internal shape reach the screen, whatever it arrived in.
  if (INTERNAL_SHAPE.test(detail)) detail = fallback

  return { detail, fields }
}

async function toError(response: Response): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    // Non-JSON error body — an HTML 500 page, an empty 502. The status text
    // will have to do, and is the safe answer anyway.
  }
  const { detail, fields } = messageFrom(body, response.status, response.statusText)
  return new ApiError(response.status, detail, fields)
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.method && init.method !== 'GET') headers.set('X-CSRFToken', csrfToken())

  const response = await fetch(`/api/portal${path}`, {
    credentials: 'same-origin',
    ...init,
    headers,
  })

  if (!response.ok) throw await toError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T,>(path: string) => request<T>(path),

  postJson: <T,>(path: string, body: unknown) =>
    request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  postForm: <T,>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form }),

  patchJson: <T,>(path: string, body: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  // PUT for the endpoints that are idempotent by design — article feedback is
  // at most one answer per person, so re-sending the same one is not a second
  // vote and a retry after a dropped connection is safe.
  putJson: <T,>(path: string, body: unknown) =>
    request<T>(path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
}

// Dev-only sign-in, mirrored from backend/core/dev_login.py. Returns false when
// the route is absent, which is the normal case once Entra is configured.
export async function devLogin(email: string): Promise<void> {
  const form = new FormData()
  form.set('email', email)
  const response = await fetch('/api/dev-login/', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': csrfToken() },
    body: form,
  })
  if (!response.ok) throw await toError(response)
}
